import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useApiKeysList, useCreateApiKey, useRevokeApiKey } from '@/hooks/useApiKeys';
import { extractApiError } from '@/lib/api';
import type { ApiKeyCreateOut, ApiKeyEnv, ApiKeyOut } from '@/lib/api-keys';
import { friendlyError } from '@/lib/error-messages';
import { Check, Copy, ExternalLink, KeyRound, Loader2, Plus } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

// Canonical PUBLIC API host (the api.* subdomain), distinct from the
// same-origin `/v1` the React app talks to. This is what external
// developers hit — keep in sync with backend API_BASE_URL + the Scalar
// docs `servers` block.
const PUBLIC_API_BASE = 'https://api.dagsight.com';
const DOCS_URL = `${PUBLIC_API_BASE}/v1/docs/api`;
const SANDBOX_CURL = `curl -X POST ${PUBLIC_API_BASE}/v1/sandbox/discoveries`;
const AUTHED_CURL = `curl ${PUBLIC_API_BASE}/v1/discoveries/JOB_ID \\
  -H "Authorization: Bearer YOUR_API_KEY"`;

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-md border border-dashed py-16 text-center">
      <KeyRound className="mb-3 h-10 w-10 text-muted-foreground" />
      <h3 className="text-base font-medium">No API keys yet</h3>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">
        No API keys yet — create one to use the DAGsight API.
      </p>
      <Button className="mt-4" onClick={onCreate}>
        <Plus className="mr-1 h-4 w-4" /> Create key
      </Button>
    </div>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = React.useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success('Copied to clipboard.');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed — select the text and copy manually.');
    }
  }

  return (
    <Button type="button" variant="outline" size="sm" onClick={copy} className="shrink-0">
      {copied ? <Check className="mr-1 h-4 w-4" /> : <Copy className="mr-1 h-4 w-4" />}
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
}

export default function ApiKeys() {
  const list = useApiKeysList();
  const create = useCreateApiKey();
  const revoke = useRevokeApiKey();

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [name, setName] = React.useState('');
  const [env, setEnv] = React.useState<ApiKeyEnv>('live');
  // Holds the just-created key (incl. full_token) so we can show it ONCE.
  const [created, setCreated] = React.useState<ApiKeyCreateOut | null>(null);
  const [pendingRevoke, setPendingRevoke] = React.useState<ApiKeyOut | null>(null);

  function openCreateDialog() {
    setName('');
    setEnv('live');
    setCreated(null);
    setDialogOpen(true);
  }

  function closeCreateDialog() {
    setDialogOpen(false);
    // Drop the full token from memory the moment the dialog closes.
    setCreated(null);
    setName('');
  }

  function submitCreate(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Name is required.');
      return;
    }
    create.mutate(
      { name: trimmed, env },
      {
        onSuccess: (key) => {
          setCreated(key);
        },
        onError: (err) => {
          const apiErr = extractApiError(err);
          toast.error(friendlyError(apiErr?.code, apiErr?.message ?? 'Failed to create key.'));
        },
      },
    );
  }

  function confirmRevoke() {
    if (!pendingRevoke) return;
    const target = pendingRevoke;
    revoke.mutate(target.id, {
      onSuccess: () => {
        toast.success(`Revoked "${target.name}"`);
        setPendingRevoke(null);
      },
      onError: (err) => {
        const apiErr = extractApiError(err);
        toast.error(friendlyError(apiErr?.code, apiErr?.message ?? 'Revoke failed.'));
        setPendingRevoke(null);
      },
    });
  }

  const items = list.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">API Keys</h1>
          <p className="text-sm text-muted-foreground">
            Authenticate requests to the DAGsight API. Treat keys like passwords.
          </p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="mr-1 h-4 w-4" /> Create key
        </Button>
      </div>

      {/* Where the docs live. Without this card a developer who just minted
          a key hits a dead end — no base URL, no reference link, no example. */}
      <div className="space-y-3 rounded-md border bg-muted/30 p-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-medium">Using the API</h2>
          <a
            href={DOCS_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            API reference <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Base URL</span>
          <code className="rounded-md border bg-background px-2 py-1 font-mono text-xs">
            {PUBLIC_API_BASE}/v1
          </code>
          <CopyButton value={`${PUBLIC_API_BASE}/v1`} />
        </div>

        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">
            Try it now — no key required (frozen SACHS demo):
          </p>
          <div className="flex items-start gap-2">
            <code className="min-w-0 flex-1 overflow-x-auto whitespace-pre rounded-md border bg-background px-3 py-2 font-mono text-xs">
              {SANDBOX_CURL}
            </code>
            <CopyButton value={SANDBOX_CURL} />
          </div>
        </div>

        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">
            With your key — authenticate via the <code className="font-mono">Authorization</code>{' '}
            header:
          </p>
          <div className="flex items-start gap-2">
            <code className="min-w-0 flex-1 overflow-x-auto whitespace-pre rounded-md border bg-background px-3 py-2 font-mono text-xs">
              {AUTHED_CURL}
            </code>
            <CopyButton value={AUTHED_CURL} />
          </div>
        </div>
      </div>

      {list.isLoading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading keys…
        </div>
      ) : list.isError ? (
        <div className="rounded-md border bg-destructive/5 p-4 text-sm text-destructive">
          Failed to load API keys — please refresh.
        </div>
      ) : items.length === 0 ? (
        <EmptyState onCreate={openCreateDialog} />
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Key</TableHead>
                <TableHead>Env</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((k) => {
                const revoked = !!k.revoked_at;
                return (
                  <TableRow key={k.id} className={revoked ? 'text-muted-foreground' : undefined}>
                    <TableCell className={revoked ? 'line-through' : 'font-medium'}>
                      {k.name}
                    </TableCell>
                    <TableCell>
                      <code className="font-mono text-xs">{k.prefix}…</code>
                    </TableCell>
                    <TableCell>
                      <Badge variant={k.env === 'live' ? 'default' : 'secondary'}>{k.env}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {fmtDate(k.created_at)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {k.last_used_at_hour ? fmtDate(k.last_used_at_hour) : 'Never'}
                    </TableCell>
                    <TableCell className="text-right">
                      {revoked ? (
                        <Badge variant="outline">Revoked</Badge>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPendingRevoke(k)}
                        >
                          Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog
        open={dialogOpen}
        onOpenChange={(o) => {
          if (!o) closeCreateDialog();
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          {created ? (
            <>
              <DialogHeader>
                <DialogTitle>API key created</DialogTitle>
                <DialogDescription>
                  Copy now — you won't see it again. We only store a hashed prefix.
                </DialogDescription>
              </DialogHeader>
              {/* Secret on a single line (scrolls if it ever overflows) with the
                  copy button inline — the GitHub/Stripe one-time-reveal pattern.
                  The wider dialog (sm:max-w-2xl) fits the ~70-char token. */}
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap rounded-md border bg-muted/40 px-3 py-2 font-mono text-xs">
                  {created.full_token}
                </code>
                <CopyButton value={created.full_token} />
              </div>
              <DialogFooter>
                <Button onClick={closeCreateDialog}>Done</Button>
              </DialogFooter>
            </>
          ) : (
            <form onSubmit={submitCreate}>
              <DialogHeader>
                <DialogTitle>Create API key</DialogTitle>
                <DialogDescription>
                  Give the key a name so you can recognise it later.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="api-key-name">Name</Label>
                  <Input
                    id="api-key-name"
                    placeholder="e.g. CI pipeline"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={create.isPending}
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="api-key-env">Environment</Label>
                  <Select
                    value={env}
                    onValueChange={(v) => setEnv(v as ApiKeyEnv)}
                    disabled={create.isPending}
                  >
                    <SelectTrigger id="api-key-env">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="live">Live</SelectItem>
                      <SelectItem value="test">Test</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeCreateDialog}
                  disabled={create.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={create.isPending}>
                  {create.isPending ? 'Creating…' : 'Create key'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!pendingRevoke} onOpenChange={(o) => !o && setPendingRevoke(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke API key?</DialogTitle>
            <DialogDescription>
              "{pendingRevoke?.name}" will stop working immediately. Any integrations using it will
              start failing. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingRevoke(null)}
              disabled={revoke.isPending}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmRevoke} disabled={revoke.isPending}>
              {revoke.isPending ? 'Revoking…' : 'Revoke'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
